
import React, { useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogCancel } from "@/components/ui/alert-dialog";
import { useCustomerKPIs } from "./customer-kpis/hooks/useCustomerKPIs";
import { KPIInputSection } from "./customer-kpis/components/KPIInputSection";
import { KPICategoryDisplay } from "./customer-kpis/components/KPICategoryDisplay";

type Account = {
  company_id?: string;
  region: string;
  country: string;
  company_name: string;
  parent_company?: string;
  industry: string;
  account_manager: string;
};

const CustomerKPIsTab = ({
  selectedAccount
}: {
  selectedAccount: Account | null;
}) => {
  const [showKPIDialog, setShowKPIDialog] = useState(false);
  
  const {
    isLoadingKPIs,
    setIsLoadingKPIs,
    rawKPIResponse,
    setRawKPIResponse,
    hasKPIContent,
    setHasKPIContent,
    kpiData,
    setKpiData,
    companyTickerSymbol,
    setCompanyTickerSymbol,
    aiProvider,
    setAiProvider,
    isOwner,
    canEdit,
    isSupervisor,
    ownershipLoaded
  } = useCustomerKPIs(selectedAccount);

  return (
    <Card className="bg-secondary/50 backdrop-blur-lg border-2 border-primary rounded-lg shadow-lg shadow-purple-500/10 my-[12px]">
      <CardContent className="mt-4">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-xl font-semibold text-white">Customer KPIs</h2>
          <p className="text-[12px] text-white">Note: AI hallucination may occur. Validation of generated information is essential.</p>
        </div>
        
        {selectedAccount && (
          <p className="text-[11px] text-primary-light mb-3">
            Selected Account ID: {selectedAccount.company_id} ({selectedAccount.company_name})
          </p>
        )}

        {isSupervisor && (
          <div className="mb-2 p-1 bg-primary/20 text-primary border-primary/30 rounded-lg">
            <div className="flex items-center justify-between">
              <p className="text-xs text-secondary-800">
                You are viewing this account as a supervisor. Only the account owner can modify KPI data.
              </p>

            </div>
          </div>
        )}

        <div className="bg-secondary/50 backdrop-blur-lg border border-white rounded-lg shadow-lg shadow-purple-500/10 w-full p-4">
          <KPIInputSection
            companyTickerSymbol={companyTickerSymbol}
            setCompanyTickerSymbol={setCompanyTickerSymbol}
            isLoadingKPIs={isLoadingKPIs}
            setIsLoadingKPIs={setIsLoadingKPIs}
            hasKPIContent={hasKPIContent}
            setHasKPIContent={setHasKPIContent}
            aiProvider={aiProvider}
            setAiProvider={setAiProvider}
            setRawKPIResponse={setRawKPIResponse}
            setKpiData={setKpiData}
            selectedAccount={selectedAccount}
            canEdit={canEdit}
          />

          <KPICategoryDisplay kpiData={kpiData} />
        </div>

        <AlertDialog open={showKPIDialog} onOpenChange={setShowKPIDialog}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>AI Response</AlertDialogTitle>
              <AlertDialogDescription className="whitespace-pre-wrap">
                {rawKPIResponse}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Close</AlertDialogCancel>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </CardContent>
    </Card>
  );
};

export default CustomerKPIsTab;
